/**
 * Cnonloggednavbar Component
 * Navigation bar for non-authenticated users
 */
'use client';
import { useEffect, type CSSProperties } from 'react';
import { Clogo } from '../Clogo';
import { useSeqLog } from '@/hooks/telemetry/use-seq-log';
import { Cbutton } from '../Cbutton';
import { Ctext } from '../Ctext';
import { Ccontainer } from '../Ccontainer';
import { ButtonPreset, TextPreset } from '@/constants/componentPresets';
import { ColorType } from '@/theme/colors';
import { FontWeight } from '@/theme/typography';
import { pxToRem } from '@/theme/spacing';
import { zIndex } from '@/theme/zIndex';
import { useViewport } from '@/hooks/dom';
import { Viewport } from '@/theme/breakpoints';
const NAVBAR_HEIGHT = 64; // pixels
const CNONLOGGEDNAVBAR_SEQ_COMPONENT = 'Cnonloggednavbar';
const CNONLOGGEDNAVBAR_MOUNTED_EVENT = 'Cnonloggednavbar mounted';
const CNONLOGGEDNAVBAR_HMR_TAG = 'v2';
export interface CnonloggednavbarProps {
    onLogoClick?: () => void;
    onPricingClick?: () => void;
    onAuthorizeClick?: () => void;
    className?: string;
    style?: CSSProperties;
}
export function Cnonloggednavbar({ onLogoClick, onPricingClick, onAuthorizeClick, className, style, }: CnonloggednavbarProps) {
    const { viewport } = useViewport();
    const isMobile = viewport === Viewport.Mobile;
    const seqLog = useSeqLog(CNONLOGGEDNAVBAR_SEQ_COMPONENT);
    useEffect(() => {
        seqLog(CNONLOGGEDNAVBAR_MOUNTED_EVENT, {
            viewport,
            isMobile,
            hmrTag: CNONLOGGEDNAVBAR_HMR_TAG,
        });
    }, [viewport, isMobile, seqLog]);
    // Navbar base style
    const navbarStyle: CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        height: pxToRem(NAVBAR_HEIGHT),
        minHeight: pxToRem(NAVBAR_HEIGHT),
        padding: '0 1rem',
        position: 'relative',
        zIndex: zIndex.navbar,
        flexShrink: 0,
        marginTop: 10,
        ...style,
    };
    // Left section style
    const leftSectionStyle: CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? 7 : 21,
    };
    // Right section style
    const rightSectionStyle: CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        gap: 16,
    };
    // Logo container style
    const logoContainerStyle: CSSProperties = {
        display: 'flex',
        alignItems: 'center',
    };
    // Authorize button inner content style
    const authorizeContentStyle: CSSProperties = {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    };
    return (
          <nav data-el="Cnonloggednavbar/Root" style={navbarStyle} className={className}>
            {/* Left Section - Logo and Pricing */}
            <div style={leftSectionStyle}>
                <Cbutton preset={ButtonPreset.Ghost} ariaLabel={onLogoClick ? "Go home" : undefined} padding={0} onClick={onLogoClick} disableHoverEffects={!onLogoClick} containerStyle={logoContainerStyle}>
                    <Clogo dataElScope="Cnonloggednavbar/Logo" size={48} withText={false}/>
                </Cbutton>

                <Cbutton preset={ButtonPreset.Ghost} onClick={onPricingClick}>
                    <Ctext size={isMobile ? 19.2 : 22} color={ColorType.White} weight={FontWeight.Medium} opacity={1}>
                        Pricing
                    </Ctext>
                </Cbutton>
            </div>

            {/* Right Section - Authorize Button */}
            <div style={rightSectionStyle}>
                <Cbutton preset={ButtonPreset.Secondary} onClick={onAuthorizeClick} padding={isMobile ? '9px 20px' : '9px 10px 9px 20px'}>
                    <div style={authorizeContentStyle}>
                        <Ctext size={isMobile ? 17.6 : 19} color={ColorType.White} weight={FontWeight.Medium} opacity={1}>
                            Authorize
                        </Ctext>
                        {!isMobile && (<div style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 10,
            }}>
                                <Ctext size={16} color={ColorType.Accent} weight={FontWeight.Medium} opacity={1}>
                                    Initial allocation
                                </Ctext>
                                <Ccontainer backgroundColor={ColorType.Black} backgroundOpacity={0.2} borderColor={ColorType.Accent} borderOpacity={0.6} glow={3} radius={20} padding="4px 18px" style={{
                boxShadow: 'rgb(102 254 193 / 17%) 0px 1px 0px 0px inset, rgb(255 255 255 / 5%) 0px 0px 0px 1px inset, rgba(0, 0, 0, 0.6) 1px 2px 5px 0px inset, rgb(102 254 193 / 15%) 0px -1px 0px 0px inset'
            }}>
                                    <Ctext preset={TextPreset.Accent} size={16} weight={600}>
                                        100
                                    </Ctext>
                                </Ccontainer>
                            </div>)}
                    </div>
                </Cbutton>
            </div>
          </nav>
        );
}
